import * as React from "react"
import {
  BookOpen,
  Briefcase,
  ClipboardList,
  Database,
  Download,
  FileText,
  Compass,
  LayoutDashboard,
  Library,
  Link2,
  ListTodo,
  MessageSquare,
  Plane,
  Plus,
  Settings,
  Shield,
  SlidersHorizontal,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { useMatch, useNavigate } from "react-router-dom"

import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@/components/ui/command"
import { useUserProfile } from "@/hooks/use-user-profile"
import { useWorkspaces } from "@/hooks/use-workspaces"
import { getFlagFromDestination } from "@/lib/countries"
import { getSanityStudioUrl } from "@/lib/sanity"
import { useShell } from "@/providers/shell-context"

function dispatchAfterNavigation(name: "cmd-add-task" | "cmd-open-extraction") {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event(name))
    })
  })
}

export function CommandPalette() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { commandPaletteOpen, setCommandPaletteOpen } = useShell()
  const { workspaces, loading: workspacesLoading } = useWorkspaces()
  const { profile } = useUserProfile()
  const isAppAdmin = profile?.is_admin === true

  const workspaceMatch = useMatch({ path: "/workspaces/:workspaceId/*", end: false })
  const workspaceId = workspaceMatch?.params.workspaceId

  const adminMatch = useMatch({ path: "/admin", end: false })
  const isAdminRoute = adminMatch != null

  const topWorkspaces = React.useMemo(() => {
    return [...workspaces]
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
      .slice(0, 5)
  }, [workspaces])

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "k" && e.key !== "K") return
      if (!(e.metaKey || e.ctrlKey)) return
      e.preventDefault()
      setCommandPaletteOpen((open) => !open)
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [setCommandPaletteOpen])

  const close = React.useCallback(() => {
    setCommandPaletteOpen(false)
  }, [setCommandPaletteOpen])

  function go(path: string) {
    close()
    navigate(path)
  }

  function goThen(path: string, eventName: "cmd-add-task" | "cmd-open-extraction") {
    close()
    navigate(path)
    dispatchAfterNavigation(eventName)
  }

  const navKeywords = (extra: string) => [extra]

  return (
    <CommandDialog
      open={commandPaletteOpen}
      onOpenChange={setCommandPaletteOpen}
      title={t("commandPalette.dialogTitle")}
      description={t("commandPalette.dialogDescription")}
      showCloseButton={false}
      className="top-[20%] max-w-lg translate-y-0 gap-0 sm:max-w-lg"
    >
      <Command className="h-auto max-h-[min(70vh,28rem)] w-full rounded-lg border-0 bg-popover p-1 shadow-none">
        <CommandInput placeholder={t("commandPalette.searchPlaceholder")} />
        <CommandList>
          <CommandEmpty>{t("commandPalette.empty")}</CommandEmpty>

          {isAdminRoute && isAppAdmin ? (
            <CommandGroup heading={t("commandPalette.adminNavigation")}>
              <CommandItem
                value={`admin home dashboard ${t("commandPalette.navAdminOverview")}`}
                keywords={navKeywords("início admin")}
                onSelect={() => go("/admin")}
              >
                <LayoutDashboard className="opacity-80" aria-hidden />
                <span>{t("commandPalette.navAdminOverview")}</span>
                <CommandShortcut>↵</CommandShortcut>
              </CommandItem>
              <CommandItem
                value={`admin settings pipeline ${t("admin.settings.nav")}`}
                keywords={navKeywords("configurações pipeline")}
                onSelect={() => go("/admin/settings")}
              >
                <SlidersHorizontal className="opacity-80" aria-hidden />
                <span>{t("admin.settings.nav")}</span>
                <CommandShortcut>↵</CommandShortcut>
              </CommandItem>
              <CommandItem
                value={`visa metadata ${t("admin.visaMetadata.nav")}`}
                keywords={navKeywords("visto")}
                onSelect={() => go("/admin/visa-metadata")}
              >
                <Database className="opacity-80" aria-hidden />
                <span>{t("admin.visaMetadata.nav")}</span>
                <CommandShortcut>↵</CommandShortcut>
              </CommandItem>
              <CommandItem
                value={`visa needs review routes ${t("admin.visaMetadata.viewNeedsReview")}`}
                keywords={navKeywords("revisão fila verificação pending flag")}
                onSelect={() => go("/admin/visa-metadata?needs_review=1")}
              >
                <ClipboardList className="opacity-80" aria-hidden />
                <span>{t("admin.visaMetadata.viewNeedsReview", { defaultValue: "Needs review" })}</span>
                <CommandShortcut>↵</CommandShortcut>
              </CommandItem>
              <CommandItem
                value={`visa csv import ${t("visaMetadata.importCsv")}`}
                keywords={navKeywords("importar csv visa")}
                onSelect={() => go("/admin/visa-metadata#csv-import")}
              >
                <Download className="opacity-80" aria-hidden />
                <span>{t("visaMetadata.importCsv")}</span>
                <CommandShortcut>↵</CommandShortcut>
              </CommandItem>
              <CommandItem
                value={`airlines ${t("admin.airlines")}`}
                keywords={navKeywords("companhias")}
                onSelect={() => go("/admin/airlines")}
              >
                <Plane className="opacity-80" aria-hidden />
                <span>{t("admin.airlines")}</span>
                <CommandShortcut>↵</CommandShortcut>
              </CommandItem>
              <CommandItem
                value={`default resources ${t("admin.resources.nav")}`}
                keywords={navKeywords("recursos links")}
                onSelect={() => go("/admin/resources")}
              >
                <Library className="opacity-80" aria-hidden />
                <span>{t("admin.resources.nav")}</span>
                <CommandShortcut>↵</CommandShortcut>
              </CommandItem>
              <CommandItem
                value={`blog sanity studio ${t("admin.blog")}`}
                keywords={navKeywords("blog conteúdo")}
                onSelect={() => {
                  close()
                  window.open(getSanityStudioUrl(), "_blank", "noopener,noreferrer")
                }}
              >
                <BookOpen className="opacity-80" aria-hidden />
                <span>{t("admin.blog")}</span>
                <CommandShortcut>↗</CommandShortcut>
              </CommandItem>
            </CommandGroup>
          ) : null}

          <CommandGroup heading={t("commandPalette.navigation")}>
            <CommandItem
              value={`${t("commandPalette.navPanel")} dashboard home`}
              keywords={navKeywords("painel início")}
              onSelect={() => go("/dashboard")}
            >
              <LayoutDashboard className="opacity-80" aria-hidden />
              <span>{t("commandPalette.navPanel")}</span>
              <CommandShortcut>↵</CommandShortcut>
            </CommandItem>
            <CommandItem
              value={`${t("nav.visaExplorer")} visa explorer nationality destination`}
              keywords={navKeywords("visto explorador rota nacionalidade destino")}
              onSelect={() => go("/dashboard/visa-explorer")}
            >
              <Compass className="opacity-80" aria-hidden />
              <span>{t("nav.visaExplorer")}</span>
              <CommandShortcut>↵</CommandShortcut>
            </CommandItem>
            {workspaceId ? (
              <>
                <CommandItem
                  value={`${t("commandPalette.navTasks")} tasks`}
                  keywords={navKeywords("tarefas")}
                  onSelect={() => go(`/workspaces/${workspaceId}/tasks`)}
                >
                  <ListTodo className="opacity-80" aria-hidden />
                  <span>{t("commandPalette.navTasks")}</span>
                  <CommandShortcut>↵</CommandShortcut>
                </CommandItem>
                <CommandItem
                  value={`${t("commandPalette.navDocuments")} documents`}
                  keywords={navKeywords("documentos")}
                  onSelect={() => go(`/workspaces/${workspaceId}/documents`)}
                >
                  <FileText className="opacity-80" aria-hidden />
                  <span>{t("commandPalette.navDocuments")}</span>
                  <CommandShortcut>↵</CommandShortcut>
                </CommandItem>
                <CommandItem
                  value={`${t("commandPalette.navResources")} links`}
                  keywords={navKeywords("recursos")}
                  onSelect={() => go(`/workspaces/${workspaceId}/resources`)}
                >
                  <Link2 className="opacity-80" aria-hidden />
                  <span>{t("commandPalette.navResources")}</span>
                  <CommandShortcut>↵</CommandShortcut>
                </CommandItem>
              </>
            ) : null}
            <CommandItem
              value={`${t("commandPalette.navSettings")} account preferences`}
              keywords={navKeywords("configurações ajustes")}
              onSelect={() => go("/settings")}
            >
              <Settings className="opacity-80" aria-hidden />
              <span>{t("commandPalette.navSettings")}</span>
              <CommandShortcut>↵</CommandShortcut>
            </CommandItem>
            {!isAdminRoute && isAppAdmin ? (
              <CommandItem
                value={`admin ${t("commandPalette.navAdminArea")}`}
                keywords={navKeywords("administrador")}
                onSelect={() => go("/admin/visa-metadata")}
              >
                <Shield className="opacity-80" aria-hidden />
                <span>{t("commandPalette.navAdminArea")}</span>
                <CommandShortcut>↵</CommandShortcut>
              </CommandItem>
            ) : null}
          </CommandGroup>

          <CommandGroup heading={t("commandPalette.quickActions")}>
            <CommandItem
              value={t("commandPalette.quickNewTask")}
              keywords={["task", "tarefa", "nova"]}
              disabled={!workspaceId}
              onSelect={() => {
                if (!workspaceId) return
                goThen(`/workspaces/${workspaceId}/tasks`, "cmd-add-task")
              }}
            >
              <Plus className="opacity-80" aria-hidden />
              <span>{t("commandPalette.quickNewTask")}</span>
              <CommandShortcut>↵</CommandShortcut>
            </CommandItem>
            <CommandItem
              value={t("commandPalette.quickImportDoc")}
              keywords={["import", "document", "extraction", "importar"]}
              disabled={!workspaceId}
              onSelect={() => {
                if (!workspaceId) return
                goThen(`/workspaces/${workspaceId}/documents`, "cmd-open-extraction")
              }}
            >
              <Download className="opacity-80" aria-hidden />
              <span>{t("commandPalette.quickImportDoc")}</span>
              <CommandShortcut>↵</CommandShortcut>
            </CommandItem>
            <CommandItem
              value={t("commandPalette.quickImportTask")}
              keywords={["import", "task", "tarefa", "extraction", "importar"]}
              disabled={!workspaceId}
              onSelect={() => {
                if (!workspaceId) return
                goThen(`/workspaces/${workspaceId}/tasks`, "cmd-open-extraction")
              }}
            >
              <Download className="opacity-80" aria-hidden />
              <span>{t("commandPalette.quickImportTask")}</span>
              <CommandShortcut>↵</CommandShortcut>
            </CommandItem>
            <CommandItem
              value={t("dashboard.createWorkspace")}
              keywords={["workspace", "novo", "plan"]}
              onSelect={() => go("/dashboard/workspaces?new=1")}
            >
              <Plus className="opacity-80" aria-hidden />
              <span>{t("dashboard.createWorkspace")}</span>
              <CommandShortcut>↵</CommandShortcut>
            </CommandItem>
          </CommandGroup>

          {!workspacesLoading && topWorkspaces.length > 0 ? (
            <CommandGroup heading={t("commandPalette.workspaces")}>
              {topWorkspaces.map((ws) => {
                const flag = getFlagFromDestination(ws.destination ?? "")
                return (
                  <CommandItem
                    key={ws.id}
                    value={`${ws.name} ${ws.destination ?? ""} workspace`}
                    keywords={[ws.destination ?? "", ws.name]}
                    onSelect={() => go(`/workspaces/${ws.id}/overview`)}
                  >
                    <span className="flex min-w-0 flex-1 items-center gap-2">
                      {flag ? (
                        <span aria-hidden>{flag}</span>
                      ) : (
                        <Briefcase className="opacity-60" aria-hidden />
                      )}
                      <span className="truncate">{ws.name}</span>
                    </span>
                    <CommandShortcut>↵</CommandShortcut>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          ) : null}

          <CommandGroup heading={t("commandPalette.help")}>
            <CommandItem
              value={t("commandPalette.helpFeedback")}
              keywords={["feedback", "email", "mailto"]}
              onSelect={() => {
                close()
                window.open("mailto:feedback@wundrlst.app", "_blank", "noopener,noreferrer")
              }}
            >
              <MessageSquare className="opacity-80" aria-hidden />
              <span>{t("commandPalette.helpFeedback")}</span>
              <CommandShortcut>↗</CommandShortcut>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </CommandDialog>
  )
}
